import { useSidebar } from '@cortex/ui';
import { ChevronDown, GraduationCap, Search } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router';
import {
  isGroupActive,
  isItemActive,
  type NavEntry,
  type NavGroup as NavGroupModel,
  type NavItem as NavItemModel,
} from '../modules/platform/index.ts';
import { projectPath } from '../modules/platform/index.ts';
import { badgeLabel, useBadges } from '../modules/identity/index.ts';
import { openSearch, START_TUTORIAL_EVENT } from '../modules/platform/index.ts';
import { prefetchNavItem } from './prefetch.ts';

export interface NavContext {
  slug: string | null;
  branch: string | null;
  /** The path after the project prefix, e.g. "data-sources/12". */
  rest: string;
}

const ROW = 'group flex items-center shrink-0 transition-colors text-white';

function NavItem({ item, ctx, compact }: { item: NavItemModel; ctx: NavContext; compact?: boolean }) {
  const { setOpenMobile } = useSidebar();
  const counts = useBadges();
  const active = isItemActive(item.page, ctx.rest);
  const badge = item.badge ? badgeLabel(counts[item.badge]) : null;
  const Icon = item.icon;
  const to = projectPath(item.page, ctx.slug, ctx.branch);
  // The pointer on the item (or the keyboard on it): its page's code and data are read ahead.
  const ahead = () => prefetchNavItem(item.page, to);
  return (
    <Link
      to={to}
      onClick={() => setOpenMobile(false)}
      onPointerEnter={ahead}
      onFocus={ahead}
      data-active={active || undefined}
      data-tour={item.tour ?? `nav-${item.page}`}
      data-testid={`nav-item-${item.page}`}
      className={`${ROW} ${compact ? 'gap-3 h-8 rounded-[14px] px-2.5 text-[13px]' : 'gap-[18px] h-9 rounded-[18px] px-3 text-sm'} no-underline hover:text-nav-hover data-[active]:bg-nav-active data-[active]:hover:text-white`}
    >
      <Icon size={compact ? 18 : 24} className="shrink-0" />
      <span className="font-medium overflow-hidden text-ellipsis whitespace-nowrap flex-1">{item.title}</span>
      {badge && (
        <span
          data-testid={`nav-badge-${item.badge}`}
          className={`ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full ${item.badgeNeutral ? 'bg-white/20' : 'bg-red-500'} text-white text-[10px] font-semibold leading-none`}
        >
          {badge}
        </span>
      )}
    </Link>
  );
}

function NavSeparator() {
  return <div className="h-px shrink-0 bg-nav-separator" data-testid="nav-separator" />;
}

function readGroupOpen(key: string) {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

/** An expandable group: opens when a child is active, and remembers the user's choice. */
function NavGroup({ group, ctx, nested }: { group: NavGroupModel; ctx: NavContext; nested?: boolean }) {
  const anyActive = isGroupActive(group, ctx.rest);
  const storageKey = `nav-group:${group.title}`;
  const [userOpen, setUserOpen] = React.useState(() => readGroupOpen(storageKey));
  const open = userOpen || anyActive;
  const slug = group.title.toLowerCase().replace(/\s+/g, '-');
  const toggle = () => {
    const next = !userOpen;
    setUserOpen(next);
    try {
      localStorage.setItem(storageKey, next ? '1' : '0');
    } catch {
      /* not persisted */
    }
  };
  const Icon = group.icon;
  return (
    <div className="flex flex-col gap-1" data-testid={`nav-group-${slug}`}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        data-active={anyActive || undefined}
        data-tour={`nav-group-${slug}`}
        className={`${ROW} ${nested ? 'gap-3 h-8 rounded-[14px] px-2.5' : 'gap-[18px] h-9 rounded-[18px] px-3'} border-0 bg-transparent cursor-pointer text-left hover:text-nav-hover data-[active]:text-nav-hover`}
      >
        <Icon size={nested ? 18 : 24} className="shrink-0" />
        <span
          className={`${nested ? 'text-[13px]' : 'text-sm'} font-medium overflow-hidden text-ellipsis whitespace-nowrap flex-1`}
        >
          {group.title}
        </span>
        <ChevronDown size={16} className={`shrink-0 transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && (
        <div className="flex flex-col gap-1 pl-6">
          {group.items.map((it) =>
            it.kind === 'group' ? (
              <NavGroup key={it.title} group={it} ctx={ctx} nested />
            ) : (
              <NavItem key={it.page} item={it} ctx={ctx} compact />
            ),
          )}
        </div>
      )}
    </div>
  );
}

/** Opens the Cmd+K palette. */
function SearchNavButton() {
  const isMac = typeof navigator !== 'undefined' && /Mac/i.test(navigator.platform || '');
  return (
    <button
      type="button"
      data-tour="nav-search"
      data-testid="nav-search"
      onClick={openSearch}
      className={`${ROW} gap-[18px] h-9 rounded-[18px] px-3 border-0 bg-transparent cursor-pointer text-left hover:text-nav-hover`}
    >
      <Search size={24} className="shrink-0" />
      <span className="text-sm font-medium overflow-hidden text-ellipsis whitespace-nowrap flex-1">
        Search
      </span>
      <kbd className="ml-auto inline-flex items-center rounded border border-white/25 px-1.5 py-0.5 text-[10px] font-semibold text-white/60">
        {isMac ? '⌘K' : 'Ctrl K'}
      </kbd>
    </button>
  );
}

function TutorialButton() {
  return (
    <button
      type="button"
      data-tour="nav-tutorial"
      data-testid="nav-tutorial"
      onClick={() => window.dispatchEvent(new CustomEvent(START_TUTORIAL_EVENT))}
      className={`${ROW} gap-[18px] h-9 rounded-[18px] px-3 border-0 bg-transparent cursor-pointer text-left hover:text-nav-hover`}
    >
      <GraduationCap size={24} className="shrink-0" />
      <span className="text-sm font-medium overflow-hidden text-ellipsis whitespace-nowrap flex-1">
        Tutorial
      </span>
    </button>
  );
}

/** Renders a nav model (shell/../nav/model.ts) into the sidebar's rows. */
export function SidebarNav({ entries, ctx }: { entries: NavEntry[]; ctx: NavContext }) {
  return (
    <nav className="flex-1 flex flex-col gap-2 overflow-y-auto min-h-0" data-testid="sidebar-nav">
      {entries.map((e, i) => {
        switch (e.kind) {
          case 'search':
            return <SearchNavButton key="search" />;
          case 'tutorial':
            return <TutorialButton key="tutorial" />;
          case 'separator':
            return <NavSeparator key={`sep-${i}`} />;
          case 'group':
            return <NavGroup key={e.title} group={e} ctx={ctx} />;
          case 'item':
            return <NavItem key={e.page} item={e} ctx={ctx} />;
        }
      })}
    </nav>
  );
}
